import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Copy, CheckCircle2, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdvectiPixelCard = () => {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('advecti_token');
      const res = await fetch(`${BACKEND_URL}/api/integrations/pixel/snippet`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) setInfo(await res.json());
    } catch (err) {
      console.error('pixel snippet fetch failed:', err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // refresh hit counter every 15s
    return () => clearInterval(t);
  }, [load]);

  const copySnippet = async () => {
    if (!info?.snippet) return;
    try {
      await navigator.clipboard.writeText(info.snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('clipboard failed:', err);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-navy/10 p-6" data-testid="advecti-pixel-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-[260px]">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
            <Activity className="text-emerald-700" size={22} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-navy text-base">Advecti Pixel</h3>
              {info?.connected ? (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700" data-testid="pixel-status-badge">
                  ✓ Receiving hits
                </span>
              ) : (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-navy/5 border border-navy/15 text-navy/70" data-testid="pixel-status-badge">
                  Not installed yet
                </span>
              )}
            </div>
            <p className="text-navy/60 text-sm mt-1">
              Advecti's native website-tracking pixel. Paste one line into your site&apos;s &lt;head&gt; tag and we
              track every visit — no Google account, no OAuth, no property selection required.
            </p>
          </div>
        </div>
      </div>

      {/* Live counters */}
      {info && (
        <div className="grid grid-cols-2 gap-3 mt-5">
          <div className="bg-cream-light/60 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wider text-navy/60 font-semibold">Hits last 24h</div>
            <div className="text-2xl font-bold text-navy mt-0.5" data-testid="pixel-hits-24h">{info.hits_24h.toLocaleString()}</div>
          </div>
          <div className="bg-cream-light/60 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wider text-navy/60 font-semibold">Visits last 7 days</div>
            <div className="text-2xl font-bold text-navy mt-0.5" data-testid="pixel-visits-7d">{info.visits_7d.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* The snippet */}
      <div className="mt-5">
        <div className="text-xs uppercase tracking-wider text-navy/60 font-semibold mb-2">Your install snippet</div>
        <div className="bg-navy text-cream rounded-lg p-3 text-xs font-mono break-all" data-testid="pixel-snippet">
          {info?.snippet || 'Loading…'}
        </div>
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <button
            onClick={copySnippet}
            disabled={!info?.snippet}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              copied ? 'bg-emerald-600 text-white' : 'bg-navy text-cream hover:bg-navy-dark'
            }`}
            data-testid="pixel-copy-btn"
          >
            {copied ? <><CheckCircle2 size={15} /> Copied</> : <><Copy size={15} /> Copy snippet</>}
          </button>
          <a
            href={info?.advertiser_id ? `/pixel-test?aid=${info.advertiser_id}` : '#'}
            target="_blank"
            rel="noreferrer"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border-2 border-navy/15 text-navy hover:bg-navy/5 ${!info?.advertiser_id ? 'opacity-50 pointer-events-none' : ''}`}
            data-testid="pixel-test-btn"
          >
            <ExternalLink size={15} /> Test pixel
          </a>
          <span className="text-xs text-navy/60">
            Paste into your website&apos;s <code className="bg-navy/10 px-1.5 py-0.5 rounded text-[11px]">&lt;head&gt;</code> tag. Works on Shopify, Squarespace, Wix, WordPress, and any custom site.
          </span>
        </div>
      </div>

      <details className="mt-4 text-sm text-navy/80" open>
        <summary className="cursor-pointer font-semibold hover:text-navy">Where do I paste this?</summary>
        <ul className="list-disc pl-5 mt-2 space-y-1.5 text-navy/75">
          <li><strong>Squarespace</strong>: Settings → Advanced → Code Injection → paste into the &ldquo;Header&rdquo; field → Save</li>
          <li><strong>Shopify</strong>: Online Store → Themes → Actions → Edit code → open <code>theme.liquid</code> → paste before &lt;/head&gt; → Save</li>
          <li><strong>Wix</strong>: Settings → Custom Code → Add Custom Code → place in &ldquo;Head&rdquo; → &ldquo;All pages&rdquo; → Save</li>
          <li><strong>WordPress</strong>: install the free &ldquo;Insert Headers and Footers&rdquo; plugin → Settings → paste in &ldquo;Scripts in Header&rdquo; → Save</li>
          <li><strong>Custom HTML</strong>: paste inside the <code>&lt;head&gt;</code> tag of every page (or once in your shared header template) → deploy</li>
        </ul>
      </details>

      <details className="mt-3 text-sm text-navy/80" open>
        <summary className="cursor-pointer font-semibold hover:text-navy">How do I know it&apos;s working?</summary>
        <ol className="list-decimal pl-5 mt-2 space-y-1.5 text-navy/75">
          <li>Open your website in an <strong>incognito / private window</strong> (so it doesn&apos;t use a cached version without the pixel).</li>
          <li>Wait 5 seconds, then close the tab.</li>
          <li>Come back here — the <strong>Hits last 24h</strong> counter above refreshes every 15 seconds.</li>
          <li>The status badge flips from <em>Not installed yet</em> to <strong>✓ Receiving hits</strong> as soon as we receive the first request.</li>
          <li>Still nothing? Click the <strong>Test pixel</strong> button — opens a tab that fires one synthetic hit and confirms our server received it. If that works but real visits don&apos;t, the script tag isn&apos;t saved on your site.</li>
        </ol>
      </details>
    </div>
  );
};

export default AdvectiPixelCard;
